import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Manages a resizable split in a CSS grid layout. Persists the ratio
 * (as a percentage of the container width) to localStorage so the
 * user's preferred layout survives reloads.
 *
 * Usage:
 *   const chat = useSplitPaneSize('ai-designer.chat', 30, 15, 55);
 *
 *   <div
 *     style={{ gridTemplateColumns: `${chat.sizePct}% 4px 1fr` }}
 *     ref={chat.containerRef}
 *   >
 *     <aside>chat</aside>
 *     <SplitDivider onDragStart={chat.startDrag} direction="vertical" />
 *     <section>canvas</section>
 *   </div>
 *
 * The divider reports mouse movement during drag back up; we convert
 * pixels into a new pct based on the container's bounding rect and
 * clamp to `[min, max]`.
 */
const STORAGE_PREFIX = 'canopy.split.';

export interface SplitPaneController {
  /** Current size of the LEFT/TOP pane as a percentage of the container. */
  readonly sizePct: number;
  /** Attach to the grid container that owns the split. Used to compute
   *  pixel → pct conversions during drag. When multiple splits share a
   *  container, attach one and pass the same element into
   *  `attachContainer` on the others. */
  readonly containerRef: React.RefObject<HTMLDivElement>;
  /** Point this split at a container ref owned elsewhere (for multi-split
   *  layouts that share a single grid). */
  readonly attachContainer: (el: HTMLDivElement | null) => void;
  /** Call from a SplitDivider's onMouseDown to begin a drag. */
  readonly startDrag: (e: React.MouseEvent) => void;
  /** Imperatively set the size (e.g. on double-click reset). */
  readonly setSize: (pct: number) => void;
  /** True while a drag is in progress — use to disable iframe pointer
   *  events, which otherwise eat the mouseup. */
  readonly isDragging: boolean;
}

export function useSplitPaneSize(
  storageKey: string,
  defaultPct: number,
  minPct = 10,
  maxPct = 80,
): SplitPaneController {
  const key = STORAGE_PREFIX + storageKey;

  const [sizePct, setSizeState] = useState<number>(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = Number(raw);
        if (Number.isFinite(parsed) && parsed >= minPct && parsed <= maxPct) return parsed;
      }
    } catch {
      /* noop */
    }
    return defaultPct;
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);

  // Persist on change.
  useEffect(() => {
    try {
      localStorage.setItem(key, String(sizePct));
    } catch {
      /* noop */
    }
  }, [key, sizePct]);

  const setSize = useCallback(
    (pct: number) => {
      const clamped = Math.max(minPct, Math.min(maxPct, pct));
      setSizeState(clamped);
    },
    [minPct, maxPct],
  );

  // Global mousemove/mouseup handlers while dragging. Attached on the
  // window so drag works even if the cursor leaves the divider.
  useEffect(() => {
    function onMove(e: MouseEvent): void {
      if (!draggingRef.current) return;
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.width <= 0) return;
      const raw = ((e.clientX - rect.left) / rect.width) * 100;
      const clamped = Math.max(minPct, Math.min(maxPct, raw));
      setSizeState(clamped);
    }
    function onUp(): void {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      setIsDragging(false);
      document.body.style.removeProperty('cursor');
      document.body.style.removeProperty('user-select');
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [minPct, maxPct]);

  const startDrag = useCallback((e: React.MouseEvent) => {
    // Left-button only; respect modifier-click (browser selection).
    if (e.button !== 0) return;
    e.preventDefault();
    draggingRef.current = true;
    setIsDragging(true);
    document.body.style.cursor = 'col-resize';
    // Prevent text selection from flickering as the user drags.
    document.body.style.userSelect = 'none';
  }, []);

  const attachContainer = useCallback((el: HTMLDivElement | null) => {
    (containerRef as { current: HTMLDivElement | null }).current = el;
  }, []);

  return { sizePct, containerRef, attachContainer, startDrag, setSize, isDragging };
}

/**
 * A variant that measures the RIGHT pane of a two-pane split. The left
 * pane fills the remainder. Useful when the right pane has a natural
 * min-width (e.g. a bindings sidebar).
 */
export function useSplitPaneSizeRight(
  storageKey: string,
  defaultPct: number,
  minPct = 10,
  maxPct = 80,
): SplitPaneController {
  const key = STORAGE_PREFIX + storageKey;

  const [sizePct, setSizeState] = useState<number>(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = Number(raw);
        if (Number.isFinite(parsed) && parsed >= minPct && parsed <= maxPct) return parsed;
      }
    } catch {
      /* noop */
    }
    return defaultPct;
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(key, String(sizePct));
    } catch {
      /* noop */
    }
  }, [key, sizePct]);

  const setSize = useCallback(
    (pct: number) => {
      const clamped = Math.max(minPct, Math.min(maxPct, pct));
      setSizeState(clamped);
    },
    [minPct, maxPct],
  );

  useEffect(() => {
    function onMove(e: MouseEvent): void {
      if (!draggingRef.current) return;
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.width <= 0) return;
      // Distance from RIGHT edge as a fraction of width.
      const raw = ((rect.right - e.clientX) / rect.width) * 100;
      const clamped = Math.max(minPct, Math.min(maxPct, raw));
      setSizeState(clamped);
    }
    function onUp(): void {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      setIsDragging(false);
      document.body.style.removeProperty('cursor');
      document.body.style.removeProperty('user-select');
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [minPct, maxPct]);

  const startDrag = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    draggingRef.current = true;
    setIsDragging(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const attachContainer = useCallback((el: HTMLDivElement | null) => {
    (containerRef as { current: HTMLDivElement | null }).current = el;
  }, []);

  return { sizePct, containerRef, attachContainer, startDrag, setSize, isDragging };
}
